import type { AvailabilityReportItem } from '../../types/reports';
import { useTheme } from '../../context/ThemeContext';

interface AvailabilitySummaryCardsProps {
  data: AvailabilityReportItem[];
  selectedTypeId?: number;
  onSelect: (item: AvailabilityReportItem) => void;
}

const COLORS = ['#2563EB', '#7C3AED', '#EA580C', '#16A34A', '#64748B'];

function getTypeLabel(item: AvailabilityReportItem) {
  return item.availabilityTypeName || item.availabilityType || 'Availability';
}

export default function AvailabilitySummaryCards({ data, selectedTypeId, onSelect }: AvailabilitySummaryCardsProps) {
  const { theme } = useTheme();

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        No availability data found.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {data.map((item, index) => {
        const typeId = item.availabilityTypeId ?? index;
        const isSelected = selectedTypeId === typeId;
        const color = COLORS[index % COLORS.length];

        return (
          <button
            key={typeId}
            type="button"
            onClick={() => onSelect(item)}
            aria-pressed={isSelected}
            className={`group relative min-h-[158px] rounded-xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              isSelected ? 'shadow-sm ring-2 ring-blue-500/15' : 'shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
            }`}
            style={{
              borderColor: isSelected ? color : theme.border,
              backgroundColor: theme.surface,
              ['--tw-ring-color' as string]: isSelected ? color : theme.primary,
            }}
          >
            <div className="mb-3 flex min-w-0 items-start gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
              <h3 className="min-h-10 text-sm font-semibold leading-5" style={{ color: theme.text }}>
                {getTypeLabel(item)}
              </h3>
            </div>

            <div className="mb-3 flex items-end gap-2">
              <p className="text-3xl font-bold leading-none tabular-nums" style={{ color: theme.text }}>
                {(item.userCount ?? item.count ?? 0).toLocaleString()}
              </p>
              <p className="mb-0 pb-0.5 text-xs" style={{ color: theme.textSecondary }}>
                experts
              </p>
            </div>

            <div className="grid grid-cols-2 border-t pt-3" style={{ borderColor: theme.border }}>
              <div>
                <p className="text-base font-semibold tabular-nums" style={{ color: theme.text }}>
                  {(item.totalAvailableHours ?? 0).toLocaleString()}
                </p>
                <p className="mt-0.5 text-[11px]" style={{ color: theme.textSecondary }}>Total hours</p>
              </div>
              <div className="border-l pl-3" style={{ borderColor: theme.border }}>
                <p className="text-base font-semibold tabular-nums" style={{ color: theme.text }}>
                  {(item.averageAvailableHours ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
                <p className="mt-0.5 text-[11px]" style={{ color: theme.textSecondary }}>Avg. hours</p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
